import { ActivityAction, ActivityRepository, RecentPage } from './types';
import { defaultActivityRepository } from './persistence';

const MAX_PAGES = 20;
const MAX_ACTIONS_PER_PAGE = 30;

let currentScope = 'default';
let repository: ActivityRepository = defaultActivityRepository;
let pages: RecentPage[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('ActivityStore listener error:', err);
    }
  });
}

export function normalizePath(rawPath: string): string {
  const withoutQuery = rawPath.split('?')[0].split('#')[0];
  const trimmed = withoutQuery.replace(/\/+$/, '');
  return trimmed || '/';
}

const IGNORED_PATHS = new Set(['/', '/login', '/403', '/404']);

export function setActivityRepository(repo: ActivityRepository): void {
  repository = repo;
  pages = repository.load(currentScope);
  notify();
}

export function setActivityScope(tenantId?: string, userId?: string): void {
  currentScope = `${tenantId || 'anon'}:${userId || 'anon'}`;
  pages = repository.load(currentScope);
  notify();
}

export function visitPage(entry: {
  path: string;
  title: string;
  suiteId: string;
  icon?: string;
  hideInRecent?: boolean;
  updateTitleOnly?: boolean;
}): void {
  const pathKey = normalizePath(entry.path);
  if (entry.hideInRecent || IGNORED_PATHS.has(pathKey)) {
    return;
  }

  const now = Date.now();
  const existingIndex = pages.findIndex((p) => p.key === pathKey);

  if (existingIndex >= 0) {
    const existing = pages[existingIndex];
    if (entry.updateTitleOnly) {
      if (entry.title && existing.title !== entry.title) {
        pages = pages.map((p, idx) => (idx === existingIndex ? { ...p, title: entry.title } : p));
        repository.save(currentScope, pages);
        notify();
      }
      return;
    }
    const updated: RecentPage = {
      ...existing,
      title: entry.title || existing.title,
      icon: entry.icon || existing.icon,
      lastVisitedAt: now,
      visits: existing.visits + 1,
    };
    pages = [updated, ...pages.filter((_, idx) => idx !== existingIndex)];
  } else {
    const newPage: RecentPage = {
      key: pathKey,
      path: pathKey,
      title: entry.title || pathKey,
      suiteId: entry.suiteId || 'crm',
      icon: entry.icon,
      firstVisitedAt: now,
      lastVisitedAt: now,
      visits: 1,
      actions: [],
    };
    pages = [newPage, ...pages].slice(0, MAX_PAGES);
  }

  repository.save(currentScope, pages);
  notify();
}

export function recordAction(
  rawPath: string,
  action: Omit<ActivityAction, 'id' | 'at'>
): void {
  const pathKey = normalizePath(rawPath);
  if (IGNORED_PATHS.has(pathKey)) return;

  const now = Date.now();
  const actionEntry: ActivityAction = {
    ...action,
    id: `act_${now}_${Math.random().toString(36).substring(2, 7)}`,
    at: now,
  };

  const existingIndex = pages.findIndex((p) => p.key === pathKey);

  if (existingIndex >= 0) {
    const page = pages[existingIndex];
    const updatedActions = [actionEntry, ...page.actions].slice(0, MAX_ACTIONS_PER_PAGE);
    const updatedPage: RecentPage = {
      ...page,
      lastVisitedAt: now,
      actions: updatedActions,
    };
    pages = [updatedPage, ...pages.filter((_, idx) => idx !== existingIndex)];
  } else {
    // Create new page entry for this action
    const newPage: RecentPage = {
      key: pathKey,
      path: pathKey,
      title: pathKey,
      suiteId: 'crm',
      firstVisitedAt: now,
      lastVisitedAt: now,
      visits: 1,
      actions: [actionEntry],
    };
    pages = [newPage, ...pages].slice(0, MAX_PAGES);
  }

  repository.save(currentScope, pages);
  notify();
}

export function clearActivity(): void {
  pages = [];
  repository.clear(currentScope);
  notify();
}

export function subscribeActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getActivitySnapshot(): RecentPage[] {
  return pages;
}
